import { useState, useEffect } from 'react';
import { useFamilyStore } from '@/store/familyStore';
import { useAuthStore } from '@/store/authStore';
import { TreeControls } from './components/TreeControls';
import { TreeCanvas } from './components/TreeCanvas';
import { TreeNodeDialog } from './components/TreeNodeDialog';
import { EmptyState } from './components/EmptyState';
import { LoadingScreen } from '@/components/common/LoadingScreen';
import { useTreeLayout } from './hooks/useTreeLayout';
import { useTreeNavigation } from './hooks/useTreeNavigation';
import { useTreeDimensions } from './hooks/useTreeDimensions';
import { TREE_CONFIG } from '@/config';
import type { FamilyMember } from '@/types';
// import FamilyTreeDownloader from '../auth/register/components/download-tree';
import FamilyNavigator from './FamilyNavigator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@radix-ui/react-tabs';
import Tree from 'react-d3-tree';
import { AlertTriangle } from 'lucide-react';

interface DialogState {
  mode: 'edit' | 'add' | 'delete' | 'add-root' | null;
  member: FamilyMember | null;
}


export function FamilyTree() {
  const { 
    members, 
    isLoading,
    fetchMembers,
    addMember,
    updateMember,
    deleteMember 
  } = useFamilyStore();

  const [isInitialized, setIsInitialized] = useState(false);

  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';

  const [dialog, setDialog] = useState<DialogState>({
    mode: null,
    member: null
  });

  // Fetch members on mount
 // Initial data fetch
 useEffect(() => {
  const initializeData = async () => {
    try {
      await fetchMembers();
      setIsInitialized(true);
    } catch (error) {
      console.error('Error fetching initial data:', error);
      // Still set initialized to true to show error state instead of infinite loading
      setIsInitialized(true);
    }
  };

  if (!isInitialized) {
    initializeData();
  }
}, [fetchMembers, isInitialized]);  

  // Get container dimensions
  const dimensions = useTreeDimensions();

  // Calculate tree layout
  const { nodePositions, contentDimensions } = useTreeLayout({
    members,
    nodeWidth: TREE_CONFIG.NODE_WIDTH,
    nodeHeight: TREE_CONFIG.NODE_HEIGHT,
    levelHeight: TREE_CONFIG.LEVEL_HEIGHT,
    frame: dimensions
  });

  // Handle navigation
 

  const handleDialogConfirm = async (names?: string[]) => {
    if (!names?.length) return;

    try {
      if (dialog.mode === 'add-root') {
        // Add root member
        await addRootMember(names[0]);
      } else if (dialog.mode === 'add' && dialog.member) {
        // Add child to existing member
        await Promise.all(names.map(name => 
          addMember({
            name,
            parentId: dialog.member!.id
          })
        ));
      } else if (dialog.mode === 'edit' && dialog.member) {
        await updateMember(dialog.member.id, { name: names[0] });
      } else if (dialog.mode === 'delete' && dialog.member) {
        await deleteMember(dialog.member.id);
      }
    } catch (error) {
      console.error('Operation failed:', error);
    }

    setDialog({ mode: null, member: null });
  };


  if (isLoading) {
    return (
      <div className="h-full flex-1 bg-gray-50 border border-border rounded-lg mt-2 flex items-center justify-center">
        <LoadingScreen />
      </div>
    );
  }


  if (!members) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-15rem)] bg-gray-50 rounded-lg">
        <div className="text-center">
        <LoadingScreen />
          <p className="mt-2 text-gray-600">Loading family tree...</p>
        </div>
      </div>
    );
  }

  return (
      <>
        
      <Tabs defaultValue="treeview" dir="rtl" className="w-full">
      <TabsList className="flex justify-between items-center">
      <TabsTrigger 
          value="treeview" 
          className="flex-1 ml-4 px-4 py-2 rounded-md data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=inactive]:bg-transparent data-[state=inactive]:border-2 data-[state=inactive]:border-gray-300 transition-all"
        >
          الشجرة
        </TabsTrigger>
        <TabsTrigger 
          value="navigator" 
          className="flex-1 px-4 py-2 rounded-md data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=inactive]:bg-transparent data-[state=inactive]:border-2 data-[state=inactive]:border-gray-300 transition-all"
        >
          تصفح
        </TabsTrigger>
      </TabsList>

      <TabsContent value="navigator" className="mt-0">
      <div className="h-full flex flex-col">
      <div 
        className="relative flex-1 bg-gray-50 overflow-hidden rounded-lg mt-2"
        style={{ minHeight: 'calc(100vh - 15rem)' }}
      >
      <FamilyNavigator
            members={members}
            onEditNode={(member) => setDialog({ mode: 'edit', member })}
            onDeleteNode={(member) => setDialog({ mode: 'delete', member })}
            onAddChild={(member) => setDialog({ mode: 'add', member })}
            isAdmin={isAdmin}
          />
      </div>
      </div>
      </TabsContent>

      <TabsContent value="treeview" className="mt-0">
      
      <div className="h-full flex flex-col">
      <div className="bg-yellow-100 p-3 mt-2 rounded-lg text-yellow-800 flex items-center justify-center gap-2">
    <AlertTriangle className="w-5 h-5" />
    <span className="font-medium">هذه الشجرة مخصصة للاستعراض وهي تجريبية.</span>
  </div>
      <div 
        className="relative flex-1 bg-gray-50 overflow-hidden border border-border rounded-lg my-2"
        style={{ minHeight: 'calc(100vh - 15rem)', maxHeight: 'calc(100vh - 15rem)' }}
      >
        <FamilyTreeComponent data={members} />
      </div>
      </div>
      </TabsContent>
    </Tabs>
    



      {dialog.mode && (
        <TreeNodeDialog
          mode={dialog.mode}
          member={dialog.member || { id: '', name: '', children: [] }}
          open={true}
          onClose={() => setDialog({ mode: null, member: null })}
          onConfirm={handleDialogConfirm}
        />
      )}
      </>
  );
}





const FamilyTreeComponent = ({ data }) => {

  if(data.length > 0){
      // Transform the flat data into a nested structure
      const transformData = (data) => {
        const map = new Map();
        data.forEach(item => map.set(item.id, { ...item, children: [] }));
      
        const tree = [];
        map.forEach(item => {
          if (item.parentId === null || item.isRoot) {
            tree.push(item);
          } else {
            const parent = map.get(item.parentId);
            if (parent) {
              parent.children.push(item);
            }
          }
        });
      
        return tree;
      };

    const treeData = transformData(data);

    return (
      <div style={{ width: '100%', height: '100vh' }}>
      <Tree
        data={treeData}
        orientation="vertical"
        pathFunc="diagonal"
        translate={{ x: (window.innerWidth / 2) * 0.50, y: 100 }}
        nodeSize={{ x: 50, y: 400 }} 
        separation={{ siblings: 2, nonSiblings: 2 }}
        zoomable={true}
        scaleExtent={{ min: 0.03, max: 2 }}
        draggable={true}
        zoom={0.037}
        renderCustomNodeElement={({ nodeDatum }) => (
          <g>
            <circle r={`${nodeDatum.name.length * 2 + 25}`} fill="#39915d" stroke="#3b2416" />
            <text 
              fill="white" 
              strokeWidth="0" 
              x="0" 
              y="0" 
              textAnchor="middle" 
              dominantBaseline="central"
            >
              {nodeDatum.name}
            </text>
          </g>
        )}
      />
    </div>
    );
  }
};